import React, { CSSProperties } from "react";
import { videoStyles } from "../../../config/styleConfig";

interface VideoExportProps {
  videoUrl?: string;
  aspectRatio?: "16:9" | "4:3" | "1:1";
  borderRadius?: number;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
}

// Default placeholder URLs that should be treated as "not set"
const PLACEHOLDER_VIDEO_URLS = ["https://www.youtube.com/watch?v=dQw4w9WgXcQ", "https://youtu.be/dQw4w9WgXcQ"];

const isPlaceholderVideoUrl = (url: string): boolean => {
  return PLACEHOLDER_VIDEO_URLS.some((placeholder) => url.includes(placeholder) || url.includes("dQw4w9WgXcQ"));
};

export const VideoExport: React.FC<VideoExportProps> = ({
  videoUrl = "",
  aspectRatio = "16:9",
  borderRadius = 8,
  autoplay = false,
  muted = true,
  loop = false,
}) => {
  const getEmbedUrl = (url: string) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      const params = new URLSearchParams();
      if (autoplay) params.set("autoplay", "1");
      if (muted) params.set("mute", "1");
      if (loop) params.set("loop", "1");
      return `https://www.youtube.com/embed/${youtubeMatch[1]}?${params.toString()}`;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
      const params = new URLSearchParams();
      if (autoplay) params.set("autoplay", "1");
      if (muted) params.set("muted", "1");
      if (loop) params.set("loop", "1");
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?${params.toString()}`;
    }
    return url;
  };

  const paddingTop =
    videoStyles.aspectRatios[aspectRatio as keyof typeof videoStyles.aspectRatios] || videoStyles.aspectRatios["16:9"];
  // Check for valid URL - exclude placeholder URLs
  const hasValidUrl = videoUrl && videoUrl.trim() !== "" && !isPlaceholderVideoUrl(videoUrl);

  const containerStyle: CSSProperties = {
    width: "100%",
    position: "relative",
    paddingTop,
    borderRadius: `${borderRadius}px`,
    overflow: "hidden",
    background: hasValidUrl ? "#000" : "#f3f4f6",
    // Create new stacking context to prevent iframe from overlapping elements below
    isolation: "isolate",
    zIndex: 0,
  };

  const placeholderStyle: CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e5e7eb",
    border: "2px dashed #9ca3af",
    borderRadius: `${borderRadius}px`,
  };

  if (!hasValidUrl) {
    return (
      <div style={containerStyle}>
        <div style={placeholderStyle}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <polygon points="10,8 16,12 10,16" fill="#6b7280" />
          </svg>
          <span style={{ fontSize: "0.875rem", color: "#6b7280", marginTop: "8px" }}>Video</span>
          <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>No video URL configured</span>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <iframe
        src={getEmbedUrl(videoUrl)}
        title="Video player"
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};
